import { useEffect, useMemo, useState } from 'react';
import type * as Y from 'yjs';

import { RegisterRow } from './register-row';
import { backendOriginToProvenance } from '@/lib/api/registers';
import type { RegisterEntry } from '@/lib/api/registers';
import type { RegisterFiltersState } from './filter-bar';
import { normalizeSeverity, type SeverityLevel } from '@/components/ui/severity-chip';
import { cn } from '@/lib/utils/cn';

/**
 * RegisterGrid — header + body wrapper for the register screen.
 *
 * Column layout mirrors `Screens.register → riskRow` in the prototype:
 *   Select · ID · Title (+ AI proposal chip / cite chips / confidence /
 *   dual-axis vendor+avero cells) · Domain · Severity · Likelihood · Owner
 *   · Status · Evidence · Origin · Action.
 *
 * Why plain `<table>` (not a third-party grid lib): the prototype renders
 * `<table class="tbl">` directly; the production visual contract is a plain
 * HTML table. Adding `@tanstack/react-table` mid-wave bloats the bundle for
 * what is at this density a sortable + filterable HTML table.
 *
 * Virtualization (perf budget — ticket §7 says ≤ 100ms perceived on a
 * 500-row engagement): when row count exceeds the threshold we slice to a
 * viewport window + render top/bottom spacer rows whose height matches the
 * rows we omit. Sufficient until ~5k rows; we swap to `react-virtual` then.
 */

const VIRTUALIZE_THRESHOLD = 200;
const ROW_HEIGHT_COMFY = 88; // taller for the dual-axis sub-row
const ROW_HEIGHT_COMPACT = 56;

export interface RegisterGridProps {
  entries: RegisterEntry[];
  /** Active filter state — applied client-side to the entries array. */
  filters: RegisterFiltersState;
  /** Density preference; flips row height + padding. */
  density: 'comfortable' | 'compact';
  /** Yjs doc when relay is connected; row swaps to YjsCell when present. */
  doc?: Y.Doc;
  /** Hide all edit affordances. */
  readOnly: boolean;
  /** Manual-save handler — wired to the route's update mutation. */
  onManualSave: (
    entryId: string,
    input: { vendor_claim?: string; avero_verification?: string },
  ) => void;
  /** Saving entry id (single-flight for now — the mutation is per-row). */
  savingEntryId: string | null;
  /** Engagement id for the citation modal. */
  engagementId: string;
}

export function RegisterGrid({
  entries,
  filters,
  density,
  doc,
  readOnly,
  onManualSave,
  savingEntryId,
  engagementId,
}: RegisterGridProps) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    setSelected((prev) => {
      const next = new Set<string>();
      const ids = new Set(entries.map((e) => e.id));
      prev.forEach((id) => {
        if (ids.has(id)) next.add(id);
      });
      return next;
    });
  }, [entries]);

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      const provenance = backendOriginToProvenance(e.origin, e.status);

      if (filters.origin === 'ai_proposed_or_confirmed') {
        if (provenance !== 'ai_proposed' && provenance !== 'ai_confirmed') return false;
      } else if (filters.origin === 'consultant_edited') {
        if (provenance !== 'consultant_edited') return false;
      } else if (filters.origin === 'manual') {
        if (provenance !== 'manual') return false;
      }

      if (filters.status !== 'all' && e.status !== filters.status) return false;
      if (filters.severity !== 'all' && severityOfEntry(e) !== filters.severity) return false;
      return true;
    });
  }, [entries, filters]);

  const rowHeight = density === 'compact' ? ROW_HEIGHT_COMPACT : ROW_HEIGHT_COMFY;
  const [viewport, setViewport] = useState({ start: 0, end: VIRTUALIZE_THRESHOLD });
  const total = filtered.length;
  const virtualize = total > VIRTUALIZE_THRESHOLD;

  const visibleRows = virtualize ? filtered.slice(viewport.start, viewport.end) : filtered;
  const topSpacerHeight = virtualize ? viewport.start * rowHeight : 0;
  const bottomSpacerHeight = virtualize ? Math.max(0, total - viewport.end) * rowHeight : 0;

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!virtualize) return;
    const scrollTop = e.currentTarget.scrollTop;
    const containerHeight = e.currentTarget.clientHeight;
    const overscan = 10;
    const start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
    const end = Math.min(total, Math.ceil((scrollTop + containerHeight) / rowHeight) + overscan);
    if (start !== viewport.start || end !== viewport.end) {
      setViewport({ start, end });
    }
  };

  useEffect(() => {
    if (virtualize) {
      setViewport({ start: 0, end: Math.min(total, VIRTUALIZE_THRESHOLD) });
    } else {
      setViewport({ start: 0, end: total });
    }
  }, [total, virtualize]);

  return (
    <div
      className={cn('overflow-auto rounded-card border border-ink-100 bg-paper-0', 'max-h-[70vh]')}
      onScroll={handleScroll}
      role="region"
      aria-label="Register entries"
      data-density={density}
    >
      <table className="w-full border-collapse text-left text-body-sm">
        <thead className="sticky top-0 z-10 bg-paper-100 text-meta uppercase tracking-wide text-ink-500">
          <tr>
            <th scope="col" className="w-10 px-3 py-2">
              <span className="sr-only">Select</span>
            </th>
            <th scope="col" className="w-20 px-2 py-2">
              ID
            </th>
            <th scope="col" className="px-2 py-2">
              Title
            </th>
            <th scope="col" className="w-32 px-2 py-2">
              Domain
            </th>
            <th scope="col" className="w-24 px-2 py-2">
              Severity
            </th>
            <th scope="col" className="w-24 px-2 py-2">
              Likelihood
            </th>
            <th scope="col" className="w-32 px-2 py-2">
              Owner
            </th>
            <th scope="col" className="w-28 px-2 py-2">
              Status
            </th>
            <th scope="col" className="w-24 px-2 py-2">
              Evidence
            </th>
            <th scope="col" className="w-44 px-2 py-2">
              Origin
            </th>
            <th scope="col" className="w-24 px-2 py-2">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-100">
          {topSpacerHeight > 0 ? (
            <tr aria-hidden="true">
              <td colSpan={11} style={{ height: topSpacerHeight, padding: 0 }} />
            </tr>
          ) : null}

          {visibleRows.map((entry) => {
            const provenance = backendOriginToProvenance(entry.origin, entry.status);
            return (
              <RegisterRow
                key={entry.id}
                entry={entry}
                provenance={provenance}
                doc={doc}
                readOnly={readOnly}
                selected={selected.has(entry.id)}
                onSelectChange={(next) => {
                  setSelected((prev) => {
                    const updated = new Set(prev);
                    if (next) updated.add(entry.id);
                    else updated.delete(entry.id);
                    return updated;
                  });
                }}
                onManualSave={(input) => onManualSave(entry.id, input)}
                saving={savingEntryId === entry.id}
                engagementId={engagementId}
              />
            );
          })}

          {bottomSpacerHeight > 0 ? (
            <tr aria-hidden="true">
              <td colSpan={11} style={{ height: bottomSpacerHeight, padding: 0 }} />
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The severity the *filter* matches on — the same word the row's Severity
 * cell shows (`<SeverityChip>` reads `payload.severity` the same way), so
 * a "High" filter selects exactly the rows the user sees as High. A row
 * with no severity, or a word outside the list, matches only "All".
 */
function severityOfEntry(entry: RegisterEntry): SeverityLevel | null {
  const payload = entry.payload as Record<string, unknown> | null;
  return payload && typeof payload === 'object' ? normalizeSeverity(payload.severity) : null;
}
